import { useCurrentWallet, useWalletBTCAddresses, useWalletProperties } from '../state'
import { AccountIdentifier } from '@dfinity/ledger-icp'
import { Principal } from '@dfinity/principal'
import ChainFusionClient from '../clients/ChainFusionClient'
import getAgent from '../utils/getAgent'
import React, { FC, PropsWithChildren, createContext, useEffect, useMemo } from 'react'
import { logger } from '../logger'
import { getBtcMinterAddress } from '../utils/getBtcMinterAddress'
import { Chain } from '../interfaces/common'

export interface Wallet {
  principalId: string
  accountId: string
  name: string
  emoji: string
  ethAddress?: string
  btcMinterAddress?: string
  btcAddress?: string
  solAddress?: string
  addressesByChain: Record<Chain, string | undefined>
}

interface WalletContextType {
  wallet: Wallet
}

export const WalletContext = createContext<WalletContextType>(null as any)

export const walletFromPrincipal = (principalId: string) => {
  const {
    walletEmojis,
    walletNames,
    walletEthAddresses,
    walletBTCMinterAddresses,
    walletSolAddresses,
  } = useWalletProperties.getState()

  const { walletBTCAddresses } = useWalletBTCAddresses.getState()

  const principal = Principal.from(principalId)
  const accountId = AccountIdentifier.fromPrincipal({ principal })

  const addressesByChain = {
    [Chain.ICP]: principalId,
    [Chain.ETH]: walletEthAddresses[principalId],
    [Chain.ARB]: walletEthAddresses[principalId],
    [Chain.BASE]: walletEthAddresses[principalId],
    [Chain.BTC]: walletBTCAddresses[principalId],
    [Chain.SOL]: walletSolAddresses[principalId],
    [Chain.ODIN]: principalId,
  }

  return {
    principalId,
    accountId: accountId.toHex(),
    name: walletNames[principalId],
    emoji: walletEmojis[principalId],
    ethAddress: walletEthAddresses[principalId],
    btcAddress: walletBTCAddresses[principalId],
    solAddress: walletSolAddresses[principalId],
    btcMinterAddress: walletBTCMinterAddresses[principalId],
    addressesByChain,
  }
}

export const ensureChainFusionAddresses = async (principalId: string) => {
  const {
    walletEthAddresses,
    setWalletEthAddress,
    walletSolAddresses,
    setWalletSolAddress,
    walletBTCMinterAddresses,
    setWalletBTCMinterAddress,
  } = useWalletProperties.getState()
  const { walletBTCAddresses, setWalletBTCAddress } = useWalletBTCAddresses.getState()

  const chainFusion = new ChainFusionClient(getAgent())
  const principal = Principal.from(principalId)

  let ethAddress: string | Promise<string> = walletEthAddresses[principalId]
  if (!ethAddress) {
    ethAddress = chainFusion.getEthAddress(principal)
  }

  let btcAddress: string | Promise<string> = walletBTCAddresses[principalId]
  if (!btcAddress) {
    btcAddress = chainFusion.getBtcAddress(principal)
  }

  let solAddress: string | Promise<string> = walletSolAddresses[principalId]
  if (!solAddress) {
    solAddress = chainFusion.getSolAddress(principal)
  }

  let btcMinterAddress: string | Promise<string> = walletBTCMinterAddresses[principalId]
  if (!btcMinterAddress) {
    btcMinterAddress = getBtcMinterAddress(principal)
  }

  await Promise.all(
    [
      { address: ethAddress, setAddress: setWalletEthAddress },
      { address: btcAddress, setAddress: setWalletBTCAddress },
      { address: solAddress, setAddress: setWalletSolAddress },
      { address: btcMinterAddress, setAddress: setWalletBTCMinterAddress },
    ].map(async ({ address, setAddress }) => {
      try {
        setAddress(principalId, await address)
      } catch (e) {
        logger.error('Failed to get address', { originalError: e })
      }
    })
  )
}

const WalletProvider: FC<PropsWithChildren> = ({ children }) => {
  const { currentWallet } = useCurrentWallet()

  const {
    walletEmojis,
    walletNames,
    walletEthAddresses,
    walletBTCMinterAddresses,
    walletSolAddresses,
  } = useWalletProperties()

  const { walletBTCAddresses } = useWalletBTCAddresses()

  useEffect(() => {
    ensureChainFusionAddresses(currentWallet)
  }, [currentWallet])

  const value: WalletContextType = useMemo(
    () => ({
      wallet: walletFromPrincipal(currentWallet),
    }),
    [
      currentWallet,
      walletEmojis,
      walletNames,
      walletBTCMinterAddresses,
      walletEthAddresses,
      walletBTCAddresses,
      walletSolAddresses,
    ]
  )

  return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>
}

export default WalletProvider
